import {List} from 'immutable';
import {camelCase, findLast} from 'lodash';
import {fieldSlug as convertFieldSlug} from '@packages/systems/dynamo/utils/SlugUtils';
import Binding, {
  type BindingType,
} from '@packages/systems/core/records/Binding';
import {getId} from '@packages/systems/core/utils/RecordUtils';
import {weakMemo} from '@packages/utilities/memo';

import {
  type BindingPathMapPojo,
  type BindingTransformer,
  type DeepBindingPathPojo,
  type ShallowBindingPathPojo,
  isDeepBindingPathPojo,
  isShallowBindingPathPojo,
} from '@packages/systems/dynamo/binding-types';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  getItemRefSlug,
  getValueFieldSlug,
  isFieldOfItemRef,
} from '@packages/systems/dynamo/utils/ParamFieldPathUtils';
import {getFieldBySlug} from '@packages/systems/dynamo/utils/CollectionUtils';
import {itemRefTargetCollectionId} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import type {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import type {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {Expression} from '@packages/systems/wfdl-ast';
import {ExpressionPath, Expressions} from '@packages/systems/wfdl';

type BindingLocationType =
  | 'generic'
  | 'commerceOrder'
  | 'searchWrapper'
  | 'userSubscription';

const CONDITION = 'condition';
const CONDITION_TO_PATH = [CONDITION];

const URL_BINDING_PATH_FRAGMENT: Record<string, string> = {
  id: 'url',
  slug: 'url',
  type: 'PlainText',
};
const NAME_BINDING_PATH_FRAGMENT: Record<string, string> = {
  id: 'name',
  slug: 'name',
  type: 'PlainText',
};

const reifyBindingsFromPojo = (
  bindingLocation: BindingLocationType
): ((bindMapPojo: BindingPathMapPojo) => List<BindingType>) =>
  weakMemo((bindMapPojo: BindingPathMapPojo) => {
    const result: BindingType[] = [];
    for (const [key, bindingPath] of Object.entries(bindMapPojo)) {
      if (isDeepBindingPathPojo(bindingPath)) {
        result.push(
          ...reifyDeepBindingWithKeyPath([key], bindingPath, bindingLocation)
        );
      } else if (isShallowBindingPathPojo(bindingPath)) {
        result.push(
          ...reifyShallowBindingWithKeyPath([key], bindingPath, bindingLocation)
        );
      }
    }
    return List(result);
  });

export const reifyGenericBindingsFromPojo = reifyBindingsFromPojo('generic');
export const reifyCommerceOrderBindingsFromPojo =
  reifyBindingsFromPojo('commerceOrder');
export const reifySearchBindingsFromPojo =
  reifyBindingsFromPojo('searchWrapper');
export const reifyUserSubscriptionBindingsFromPojo =
  reifyBindingsFromPojo('userSubscription');

function reifyDeepBindingWithKeyPath(
  keyPath: string[],
  desc: DeepBindingPathPojo,
  bindingLocation: BindingLocationType
): BindingType[] {
  return Object.entries(desc).flatMap(([key, val]) =>
    reifyShallowBindingWithKeyPath(keyPath.concat(key), val, bindingLocation)
  );
}

function reifyShallowBindingWithKeyPath(
  keyPath: string[],
  desc: ShallowBindingPathPojo,
  bindingLocation: BindingLocationType
): BindingType[] {
  return [reifyBinding(desc, keyPath, bindingLocation)];
}

function reifyBinding(
  bindingPath: ShallowBindingPathPojo,
  keyPath: string[],
  bindingLocation: BindingLocationType
): BindingType {
  const type = getBindingPathFieldTypePojo(bindingPath);
  const innerType = getBindingPathFieldInnerTypePojo(bindingPath) ?? null;
  const from = reifyFromPath(
    bindingPath,
    keyPath,
    type,
    innerType,
    bindingLocation
  );
  const to = keyPath;
  const firstPart = bindingPath[0];
  const id = firstPart?.['id'] ?? '';
  return Binding({
    id,
    to,
    from,
    type,
    filter: reifyFilter(bindingPath, keyPath),
  });
}

function getFromPathRootPath(
  bindingPath: ShallowBindingPathPojo,
  type: string
): ShallowBindingPathPojo {
  const firstFragmentOfTypeIndex = bindingPath.findIndex(
    (part) => part.type === type
  );
  return bindingPath.slice(0, firstFragmentOfTypeIndex + 1);
}

function reifyFromPath(
  bindingPath: ShallowBindingPathPojo,
  toPath: Array<string>,
  type: string,
  innerType: null | string,
  bindingLocation: string | BindingLocationType
): string[] {
  let newPath: ShallowBindingPathPojo;
  const first = toPath[0];
  const rootPath = getFromPathRootPath(bindingPath, type);
  const isSrc = first === 'src';
  const isMedia = first === 'media';
  const isRepeater = first === 'repeater';

  // If the field type is `Set` we want to use the `innerType`
  // for defining what's the fromPath supposed to be like.
  const finalType = type === 'Set' ? innerType : type;
  switch (finalType) {
    case 'Video': {
      newPath = isMedia ? rootPath : [...rootPath, URL_BINDING_PATH_FRAGMENT];
      break;
    }
    case 'ImageRef': {
      newPath =
        isSrc || isMedia || isRepeater
          ? rootPath
          : [...rootPath, URL_BINDING_PATH_FRAGMENT];
      break;
    }
    case 'FileRef': {
      newPath = [...rootPath, URL_BINDING_PATH_FRAGMENT];
      break;
    }
    case 'Option': {
      newPath = [...rootPath, NAME_BINDING_PATH_FRAGMENT];
      break;
    }
    default: {
      newPath = bindingPath;
      break;
    }
  }
  return newPath.map((part, index) => {
    const slug = String(part.slug ?? '');
    if (
      isMetadataBindingPartPojo(part) ||
      (bindingLocation === 'commerceOrder' && index === 0)
    ) {
      return slug;
    } else if (bindingLocation === 'searchWrapper') {
      return camelCase(slug);
    } else if (slug === 'inventory' && part.type === 'CommerceInventory') {
      return slug;
    } else if (slug === 'ecSkuInventoryQuantity') {
      return slug;
    } else if (bindingLocation === 'userSubscription') {
      return camelCase(slug);
    } else if (slug === 'fullSlug') {
      return slug;
    } else {
      return convertFieldSlug(slug);
    }
  });
}

const getLastNonMetadataBindingPartPojo = (
  bindingPath: ShallowBindingPathPojo
): Record<string, string> => {
  const notFound: Record<string, string> = {};
  for (let i = bindingPath.length - 1; i >= 0; i--) {
    const part = bindingPath[i];
    if (!part) {
      return notFound;
    }
    if (!isMetadataBindingPartPojo(part)) {
      return part;
    }
  }

  return notFound;
};

const underscoreIdPath: ExpressionPath = [{in: 'Record', at: '_id'}];
const idPath: ExpressionPath = [{in: 'Record', at: 'id'}];
const slugPath: ExpressionPath = [{in: 'Record', at: 'slug'}];
const typePath: ExpressionPath = [{in: 'Record', at: 'type'}];

const tryGetExpressionTextAtPath = (
  expr: Expression | void,
  path: ExpressionPath
): string | null => {
  const value = expr ? Expressions.getIn(expr, path) : null;
  return value ? Expressions.getText(value) : null;
};

// isMetadataBindingPart in Expressions
const isMetadataBindingPart = (item: Expression) => {
  const underscoreid = tryGetExpressionTextAtPath(item, underscoreIdPath);
  const id = tryGetExpressionTextAtPath(item, idPath);
  const slug = tryGetExpressionTextAtPath(item, slugPath);
  const idText = id || underscoreid;
  return idText === slug && slug !== 'slug';
};

function getLastNonMetadataBindingPart(
  bindingPath: Expression
): Expression | void {
  const bindingPathArray = Expressions.getList(bindingPath);
  if (bindingPathArray) {
    return findLast(bindingPathArray, (item) => !isMetadataBindingPart(item));
  }
}

export const getBindingPathFieldSlug = (
  bindingPath: Expression
): string | null => {
  const lastEntry = getLastNonMetadataBindingPart(bindingPath);
  return tryGetExpressionTextAtPath(lastEntry, slugPath);
};

export const getBindingPathFieldType = (bindingPath: Expression): string => {
  const lastEntry = getLastNonMetadataBindingPart(bindingPath);
  return tryGetExpressionTextAtPath(lastEntry, typePath) ?? 'PlainText';
};

export const getBindingPathFieldTypePojo = (
  bindingPath: ShallowBindingPathPojo
): string => {
  return getLastNonMetadataBindingPartPojo(bindingPath).type ?? 'PlainText';
};

export const getBindingPathFieldInnerTypePojo = (
  bindingPath: ShallowBindingPathPojo
): string | undefined => {
  return getLastNonMetadataBindingPartPojo(bindingPath).innerType;
};

const reifyFilter = (
  bindingPath: ShallowBindingPathPojo,
  keyPath: string[]
): BindingTransformer => {
  // We don't need to rely on style filters in the node
  // bindings as the filter can always be derived from
  // the binding's keyPath.
  const lastBindingFragment = bindingPath[bindingPath.length - 1];
  const lastBindingFragmentType = lastBindingFragment?.type ?? 'PlainText';

  if (keyPath[0] === 'style') {
    return {
      type: 'style',
      params: keyPath.slice(1),
    };
  } else if (lastBindingFragmentType === 'RichText') {
    return {
      type: 'rich',
      params: emptyArray,
    };
  } else if (lastBindingFragmentType === 'CommercePrice') {
    return {
      type: 'price',
      params: emptyArray,
    };
  }
  const [type, ...params] = (lastBindingFragment?.filter ?? 'identity').split(
    '|'
  );
  return {
    // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
    type,
    params,
  };
};

const isMetadataBindingPartPojo = (part: Record<string, string>): boolean => {
  const id = getId(part);
  const slug = part.slug;
  // We need to check for slug because some dataWHref bindings can have
  // detail page links to itemRef page, where binding path id is `slug`.
  return id === slug && slug !== 'slug';
};

export const reifyConditions = weakMemo(
  (bindableCollection: CollectionRecordType) =>
    weakMemo((collections: CollectionsType) =>
      weakMemo(
        (conditionFields: Record<string, unknown>): List<BindingType> => {
          return Object.keys(conditionFields).reduce(
            (conditionList: List<BindingType>, fieldSlugPath: string) => {
              const to = CONDITION_TO_PATH;
              if (isFieldOfItemRef(fieldSlugPath)) {
                const itemRefSlug = getItemRefSlug(fieldSlugPath);
                const valueFieldSlug = getValueFieldSlug(fieldSlugPath);
                const itemRefField = itemRefSlug
                  ? getFieldBySlug(bindableCollection, itemRefSlug)
                  : null;
                const itemRefCollectionId = itemRefField
                  ? itemRefTargetCollectionId(itemRefField)
                  : null;
                const valueFieldCollection = itemRefCollectionId
                  ? collections.get(itemRefCollectionId)
                  : null;
                const valueField = valueFieldCollection
                  ? getFieldBySlug(valueFieldCollection, valueFieldSlug)
                  : null;
                const valueFieldType = valueField
                  ? valueField.innerType || valueField.type
                  : null;
                const from = [
                  String(itemRefSlug),
                  convertFieldSlug(valueFieldSlug),
                ];
                return valueFieldType
                  ? conditionList.push(
                      Binding({
                        type: valueFieldType,
                        from,
                        to,
                      })
                    )
                  : conditionList;
              } else {
                const fieldSlug = getValueFieldSlug(fieldSlugPath);
                const field = getFieldBySlug(bindableCollection, fieldSlug);
                const fieldType = field ? field.innerType || field.type : null;
                const from = [fieldSlug];
                return fieldType
                  ? conditionList.push(
                      Binding({
                        type: fieldType,
                        from,
                        to,
                      })
                    )
                  : conditionList;
              }
            },
            List()
          );
        }
      )
    )
);
